import * as ModelEvent from "@node-elion/syncron";
import { compact } from "lodash";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import stringToUndefined from "../../utils/stringToUndefined";
import createLogger from "../../utils/logger.util";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import Subscription from "../../types/Subscription";
import { ModelId } from "../../types/ModelId";
import { Base, Language as Lang } from "..";

const logger = createLogger({ name: "PaymentSystem" });

const stringifyError = (err: unknown): string => {
	if (err instanceof Error) {
		return err.message; //  `${err.name}: ${err.message}`
	}
	try {
		return JSON.stringify(err);
	} catch {
		return String(err); // fallback на toString()
	}
};

class PaymentSystem extends Base {
	private static optionsToRequest(optns: PaymentSystem.SubscribeOptions) {
		return {
			lang: optns.language,
			limit: optns.limit,
			offset: optns.offset,
			types: optns.types,
		};
	}

	public static fromResponseMaid(
		data: any,
	): PaymentSystem.MaibPaymentConfiguration {
		const { configuration, paymentProvider } =
			data as PaymentSystem.Response;

		const config = configuration as PaymentSystem.MaibPaymentConfiguration;

		const isExecutorAndMaib =
			config &&
			data.type === PaymentSystemFrontEndType.EXECUTOR &&
			paymentProvider === PaymentProvider.MAIB;

		const payload: PaymentSystem.MaibPaymentConfiguration = {
			maibPaymentsKey: "",
			maibPaymentsSecretKey: "",
			maibSignatureId: "",

			maibPaymentsCurrency: MaibPaymentCurrency.MDL,
			maibPaymentsCountryIso: MaibPaymentCountryIso.MDA,

			systemReturnFeeToExecutor: false,
			systemFeePercentageForExecutor: 0,
			systemFeeAdditionalForExecutor: 0,

			maibCheck: false,
			maibAction: false,

			maibWithdrawMethod: WithdrawMethod.WITHDRAW_WITH_HOLD,
		};

		if (config?.maibCheck || isExecutorAndMaib) {
			payload.maibPaymentsKey = config.maibPaymentsKey;
			payload.maibPaymentsSecretKey = config.maibPaymentsSecretKey;
			payload.maibSignatureId = config.maibSignatureId;

			payload.maibPaymentsCurrency = config.maibPaymentsCurrency;
			payload.maibPaymentsCountryIso = config.maibPaymentsCountryIso;

			payload.systemReturnFeeToExecutor =
				config.systemReturnFeeToExecutor;
			payload.systemFeePercentageForExecutor =
				config.systemFeePercentageForExecutor;
			payload.systemFeeAdditionalForExecutor =
				config.systemFeeAdditionalForExecutor;

			payload.maibCheck = config.maibCheck;
			payload.maibAction = config.maibAction;
		}

		return payload;
	}

	public static fromResponseAsset(
		data: any,
	): PaymentSystem.AssetPaymentConfiguration {
		const { configuration, paymentProvider } =
			data as PaymentSystem.Response;

		const config = configuration as PaymentSystem.AssetPaymentConfiguration;

		const isExecutorAndAsset =
			config &&
			data.type === PaymentSystemFrontEndType.EXECUTOR &&
			paymentProvider === PaymentProvider.ASSETPAYMENT;

		const payload: PaymentSystem.AssetPaymentConfiguration = {
			assetPaymentsFeePercentage: 0,
			assetPaymentsAdditionalFee: 0,
			assetPaymentsGlobalFeeFromSystem: false,
			assetPaymentsProcessingId: 0,
			assetPaymentsCurrency: PaymentCurrency.UAH,
			assetPaymentsCountryIso: PaymentCountryIso.UKR,
			assetPaymentsKey: "",
			assetPaymentsSecretKey: "",

			systemReturnFeeToExecutor: false,
			systemFeePercentageForExecutor: 0,
			systemFeeAdditionalForExecutor: 0,

			assetCheck: false,
			assetAction: false,
		};

		if (config?.assetCheck || isExecutorAndAsset) {
			payload.assetPaymentsGlobalFeeFromSystem =
				config.assetPaymentsGlobalFeeFromSystem;
			payload.assetPaymentsFeePercentage =
				config.assetPaymentsFeePercentage;
			payload.assetPaymentsAdditionalFee =
				config.assetPaymentsAdditionalFee;

			payload.assetPaymentsProcessingId =
				config.assetPaymentsProcessingId;

			payload.assetPaymentsCurrency = config.assetPaymentsCurrency;
			payload.assetPaymentsCountryIso = config.assetPaymentsCountryIso;
			payload.assetPaymentsKey = config.assetPaymentsKey;
			payload.assetPaymentsSecretKey = config.assetPaymentsSecretKey;

			payload.systemReturnFeeToExecutor =
				config.systemReturnFeeToExecutor;
			payload.systemFeePercentageForExecutor =
				config.systemFeePercentageForExecutor;
			payload.systemFeeAdditionalForExecutor =
				config.systemFeeAdditionalForExecutor;

			payload.assetCheck = config.assetCheck;
			payload.assetAction = config.assetAction;
		}

		return payload;
	}

	public static fromResponseBase(data: any): PaymentSystem.BaseModel {
		const { paymentProvider, paymentAccounts } =
			data as PaymentSystem.Response;

		const DEFAULT_NAME = "asset-payments_empty_customer";

		const payload: PaymentSystem.BaseModel = {
			id: data.id,
			name: data.name === DEFAULT_NAME ? "" : data.name,
			active: typeof data.active === "boolean" ? data.active : false,

			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,

			isDefault: data.isDefault,
			type: data.type,

			withdrawMethod: data.withdrawMethod,
			defaultHoldValue: data.defaultHoldValue,

			paymentProvider,
			prePaymentProvider: paymentProvider,

			paymentAccounts,

			taxiServiceIds:
				compact(
					paymentAccounts?.map(({ taxiService }) => taxiService?.id),
				) || [],
			taxiServices:
				compact(
					paymentAccounts?.map(({ taxiService }) => taxiService),
				) || [],

			companyIds:
				compact(
					paymentAccounts?.map(({ company, taxiService }) => {
						if (company) return company?.id;
						return taxiService?.company?.id;
					}),
				) || [],

			companies:
				compact(
					paymentAccounts?.map(({ company, taxiService }) => {
						if (company) return company;
						return taxiService?.company;
					}),
				) || [],
		};

		return payload;
	}

	public static fromResponse(data: any): PaymentSystem.Model {
		const payload: PaymentSystem.Model = {
			...PaymentSystem.fromResponseBase(data),
			...PaymentSystem.fromResponseAsset(data),
			...PaymentSystem.fromResponseMaid(data),
		};

		return payload;
	}

	public static toRequest(
		data: Omit<PaymentSystem.Modified, "id" | "updateDefault">,
	): Omit<PaymentSystem.Modified, "id" | "updateDefault"> {
		const DEFAULT_NAME = "Uncknow";

		const payload: Omit<PaymentSystem.Modified, "id" | "updateDefault"> & {
			configuration: any;
		} = {
			active: typeof data.active === "boolean" ? data.active : false,
			name: stringToUndefined(data.name) ?? DEFAULT_NAME,
			paymentProvider:
				data.prePaymentProvider ?? PaymentProvider.ASSETPAYMENT,
			type: data.type ?? PaymentSystemFrontEndType.CUSTOMER,

			withdrawMethod:
				data.withdrawMethod ?? WithdrawMethod.WITHDRAW_WITH_HOLD,

			defaultHoldValue: data.defaultHoldValue ?? 0.0,

			configuration: {},
		};

		const [isCustomer, isExecutor] = [
			data.type === PaymentSystemFrontEndType.CUSTOMER,
			data.type === PaymentSystemFrontEndType.EXECUTOR,
		];

		if (isCustomer) {
			payload.companyIds = data.companyIds;

			if (data?.assetCheck) {
				const config = data as PaymentSystem.AssetPaymentConfiguration;

				payload.configuration.assetPaymentsGlobalFeeFromSystem =
					config.assetPaymentsGlobalFeeFromSystem;
				payload.configuration.assetPaymentsFeePercentage =
					config.assetPaymentsFeePercentage;
				payload.configuration.assetPaymentsAdditionalFee =
					config.assetPaymentsAdditionalFee;

				payload.configuration.assetPaymentsProcessingId =
					config.assetPaymentsProcessingId;

				payload.configuration.assetPaymentsCurrency =
					config.assetPaymentsCurrency;
				payload.configuration.assetPaymentsCountryIso =
					config.assetPaymentsCountryIso;
				payload.configuration.assetPaymentsKey =
					config.assetPaymentsKey.trim();
				payload.configuration.assetPaymentsSecretKey =
					config.assetPaymentsSecretKey.trim();

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.assetCheck = config.assetCheck;
				payload.configuration.assetAction = config.assetAction;

				if (isExecutor) {
					payload.configuration.systemFeeAdditionalForExecutor =
						config.systemFeeAdditionalForExecutor;

					payload.configuration.systemFeePercentageForExecutor =
						config.systemFeePercentageForExecutor;
				}
			}

			if (data?.maibCheck) {
				const config = data as PaymentSystem.MaibPaymentConfiguration;

				payload.configuration.maibPaymentsKey = config.maibPaymentsKey;
				payload.configuration.maibPaymentsSecretKey =
					config.maibPaymentsSecretKey;
				payload.configuration.maibSignatureId = config.maibSignatureId;

				payload.configuration.maibPaymentsCurrency =
					config.maibPaymentsCurrency;
				payload.configuration.maibPaymentsCountryIso =
					config.maibPaymentsCountryIso;

				payload.configuration.maibCheck = config.maibCheck;
				payload.configuration.maibAction = config.maibAction;

				payload.configuration.maibWithdrawMethod =
					WithdrawMethod.WITHDRAW_WITH_HOLD;
			}

			if (data.maibAction) {
				payload.paymentProvider = PaymentProvider.MAIB;
			}
			if (data.assetAction) {
				payload.paymentProvider = PaymentProvider.ASSETPAYMENT;
			}
			if (!data.maibCheck && !data.assetCheck) {
				payload.active = false;
			}
		}

		if (isExecutor) {
			payload.taxiServiceIds = data.taxiServiceIds;

			//   if (data.paymentProvider === PaymentProvider.ASSETPAYMENT) {
			// 	const config = data as PaymentSystem.AssetPaymentConfiguration;
			// 	const isExecutor = data.type === PaymentSystemFrontEndType.EXECUTOR;

			// 	payload.configuration.assetPaymentsGlobalFeeFromSystem =
			// 		config.assetPaymentsGlobalFeeFromSystem;
			// 	payload.configuration.assetPaymentsFeePercentage =
			// 		config.assetPaymentsFeePercentage;
			// 	payload.configuration.assetPaymentsAdditionalFee =
			// 		config.assetPaymentsAdditionalFee;

			// 	payload.configuration.assetPaymentsProcessingId =
			// 		config.assetPaymentsProcessingId;

			// 	payload.configuration.assetPaymentsCurrency =
			// 		config.assetPaymentsCurrency;
			// 	payload.configuration.assetPaymentsCountryIso =
			// 		config.assetPaymentsCountryIso;
			// 	payload.configuration.assetPaymentsKey =
			// 		config.assetPaymentsKey.trim();
			// 	payload.configuration.assetPaymentsSecretKey =
			// 		config.assetPaymentsSecretKey.trim();

			// 	payload.configuration.systemReturnFeeToExecutor =
			// 		config.systemReturnFeeToExecutor;
			// 	if (isExecutor) {
			// 		payload.configuration.systemFeeAdditionalForExecutor =
			// 			config.systemFeeAdditionalForExecutor;

			// 		payload.configuration.systemFeePercentageForExecutor =
			// 			config.systemFeePercentageForExecutor;
			// 	}
			// }

			if (payload.paymentProvider === PaymentProvider.ASSETPAYMENT) {
				const config = data as PaymentSystem.AssetPaymentConfiguration;

				payload.configuration.assetPaymentsGlobalFeeFromSystem =
					config.assetPaymentsGlobalFeeFromSystem;
				payload.configuration.assetPaymentsFeePercentage =
					config.assetPaymentsFeePercentage;
				payload.configuration.assetPaymentsAdditionalFee =
					config.assetPaymentsAdditionalFee;

				payload.configuration.assetPaymentsProcessingId =
					config.assetPaymentsProcessingId;

				payload.configuration.assetPaymentsCurrency =
					config.assetPaymentsCurrency;
				payload.configuration.assetPaymentsCountryIso =
					config.assetPaymentsCountryIso;
				payload.configuration.assetPaymentsKey =
					config.assetPaymentsKey.trim();
				payload.configuration.assetPaymentsSecretKey =
					config.assetPaymentsSecretKey.trim();

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				// payload.configuration.assetCheck = config.assetCheck;
				// payload.configuration.assetAction = config.assetAction;

				payload.configuration.systemFeeAdditionalForExecutor =
					config.systemFeeAdditionalForExecutor;

				payload.configuration.systemFeePercentageForExecutor =
					config.systemFeePercentageForExecutor;
			}

			if (payload.paymentProvider === PaymentProvider.MAIB) {
				const config = data as PaymentSystem.MaibPaymentConfiguration;

				payload.configuration.maibPaymentsKey = config.maibPaymentsKey;
				payload.configuration.maibPaymentsSecretKey =
					config.maibPaymentsSecretKey;
				payload.configuration.maibSignatureId = config.maibSignatureId;

				payload.configuration.maibPaymentsCurrency =
					config.maibPaymentsCurrency;
				payload.configuration.maibPaymentsCountryIso =
					config.maibPaymentsCountryIso;

				// payload.configuration.maibCheck = config.maibCheck;
				// payload.configuration.maibAction = config.maibAction;

				payload.configuration.maibWithdrawMethod =
					WithdrawMethod.WITHDRAW_WITH_HOLD;
			}
		}

		logger.info("[PaymentSystem] toRequest", {
			payload,
			data,
		});

		return payload;
	}

	public static toRequestForCheckConnection(
		data: Omit<PaymentSystem.Modified, "id" | "updateDefault">,
	): PaymentSystem.CheckConnectionRequestPayload | null {
		if (data.paymentProvider === PaymentProvider.ASSETPAYMENT) {
			const config = data as PaymentSystem.AssetPaymentConfiguration;

			const payload: PaymentSystem.AssetCheckConnection = {
				provider: PaymentProvider.ASSETPAYMENT,
				configuration: {
					assetPaymentsKey: config.assetPaymentsKey.trim(),
					assetPaymentsSecretKey:
						config.assetPaymentsSecretKey.trim(),
					assetPaymentsProcessingId: config.assetPaymentsProcessingId,
					assetPaymentsCurrency: config.assetPaymentsCurrency,
					assetPaymentsCountryIso: config.assetPaymentsCountryIso,
				},
			};

			logger.info("[PaymentSystem] toRequestForCheckConnection", {
				payload,
				data,
			});

			return payload;
		}

		if (data.paymentProvider === PaymentProvider.MAIB) {
			const config = data as PaymentSystem.MaibPaymentConfiguration;

			const payload: PaymentSystem.MaibCheckConnection = {
				provider: PaymentProvider.MAIB,
				configuration: {
					maibPaymentsKey: config.maibPaymentsKey,
					maibPaymentsSecretKey: config.maibPaymentsSecretKey,
					maibSignatureId: config.maibSignatureId,
					maibPaymentsCurrency: config.maibPaymentsCurrency,
					maibPaymentsCountryIso: config.maibPaymentsCountryIso,
				},
			};

			logger.info("[PaymentSystem] toRequestForCheckConnection", {
				payload,
				data,
			});

			return payload;
		}

		return null;
	}

	public static async connectCheck(params: PaymentSystem.Model): Promise<{
		result: boolean | null;
		error: string | null;
	}> {
		try {
			const data = this.toRequestForCheckConnection(params);

			if (!data) {
				logger.info("[PaymentSystem] connectCheck", null);
				return { result: null, error: "don`n normal formating data" };
			}

			const res = await this.request((prpc) =>
				prpc.theirsModel.payment.checkConnection(data),
			);

			const retval = {
				result: res?.success || null,
				error: res?.message || null,
			};

			logger.info("[PaymentSystem] connectCheck", retval);
			return retval;
		} catch (err) {
			const errorMessage = stringifyError(err);
			logger.error("[PaymentSystem] Error connectCheck", errorMessage);
			return { result: null, error: errorMessage };
		}
	}

	public static async update({
		id,
		updateDefault = false,
		...params
	}: PaymentSystem.Modified): Promise<{
		result: PaymentSystem.Model | null;
		error: string | null;
	}> {
		try {
			if (!id) return { result: null, error: "Missing ID" };
			const toRequest = this.toRequest(params);
			logger.info("[PaymentSystem]  update params", {
				id,
				params,
				toRequest,
			});

			const result = await this.request((prpc) =>
				prpc.theirsModel.payment.system.update(
					id,
					toRequest,
					updateDefault,
				),
			);
			logger.info("[PaymentSystem] update res", {
				result,
				params,
				toRequest,
			});
			return { result, error: null };
		} catch (err) {
			const errorMessage = stringifyError(err);
			logger.error("[PaymentSystem] Error update", errorMessage);
			return { result: null, error: errorMessage };
		}
	}

	public static async create({
		id,
		...params
	}: PaymentSystem.Modified): Promise<{
		result: PaymentSystem.Model | null;
		error: string | null;
	}> {
		try {
			const toRequest = this.toRequest(params);
			logger.info("[PaymentSystem]  create params", {
				id,
				params,
				toRequest,
			});

			const res = await this.request((prpc) =>
				prpc.theirsModel.payment.system.create(toRequest, {
					templateId: id ?? 0,
				}),
			);
			logger.info("[PaymentSystem]  create res", {
				res,
				params,
				toRequest,
			});
			return { result: res, error: null };
		} catch (err) {
			const errorMessage = stringifyError(err);
			logger.error("[PaymentSystem] Error create", errorMessage);
			return { result: null, error: errorMessage };
		}
	}

	public static async store(id: number): Promise<PaymentSystem.Model | null> {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.payment.system.getById(id),
			);

			return res;
		} catch (err) {
			logger.error("[PaymentSystem] Error store", err);
			return null;
		}
	}

	public static async copy(id: number): Promise<PaymentSystem.Model | null> {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.payment.system.copy(id),
			);
			logger.info("[PaymentSystem]  copy res", res);
			return res;
		} catch (err) {
			logger.error("[PaymentSystem] Error copy", err);
			return null;
		}
	}

	public static async destroy(
		ids: number[],
	): Promise<PaymentSystem.Model | null> {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.payment.system.delete(ids),
			);

			return res;
		} catch (err) {
			logger.error("[PaymentSystem] Error destroy", err);
			return null;
		}
	}

	public static async subscribe(
		options: PaymentSystem.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<PaymentSystem.Model>,
	): Promise<Subscription<PaymentSystem.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				logger.info("[PaymentSystem] subscribe", state);
				onUpdate({
					...state,
					models: state.models.map(this.fromResponse),
				});
			},
		});
		const params = this.optionsToRequest(options);
		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.payment.system.subscribe({
					params,
					ping: () => true,
					onEvent: async (events) => {
						await modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error("[PaymentSystem] Error subscribe", error);
					},
				}),
			{
				name: "PaymentSystem.subscribe",
				metadata: params,
			},
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (subOptions: PaymentSystem.SubscribeOptions) =>
				subscription.update(this.optionsToRequest(subOptions)),
		} as Subscription<PaymentSystem.SubscribeOptions>;
	}
}
declare namespace PaymentSystem {
	interface BaseModel
		extends NonEditableProperties,
			Pick<
				Response,
				| "active"
				| "isDefault"
				| "type"
				| "paymentProvider"
				| "prePaymentProvider"
			> {
		name: string;

		paymentAccounts: PaymentAccount[];

		taxiServiceIds: number[];
		taxiServices: PaymentAccount["taxiService"][];

		companyIds: number[];
		companies: PaymentAccount["company"][];

		updateDefault?: boolean;

		withdrawMethod: WithdrawMethod;
		defaultHoldValue: number;
	}

	interface Model
		extends BaseModel,
			AssetPaymentConfiguration,
			MaibPaymentConfiguration {}

	interface SubscribeOptions
		extends Omit<
			ServiceSubscribeOptionsBase<PaymentSystem.Model>,
			"query"
		> {
		types: PaymentSystemFrontEndType[];
		/** Only for `executor` */
		taxiServiceIds?: number[];
		/** Only for `customer` */
		companyIds?: number[];
		language?: Lang;
	}

	type NonEditablePropertyNames =
		| "id"
		| "createdAt"
		| "updatedAt"
		| "deletedAt";

	interface NonEditableProperties {
		readonly id: ModelId;
		readonly createdAt: string;
		readonly updatedAt: string;
		readonly deletedAt: string | null;
	}

	type New = Omit<Model, NonEditablePropertyNames>;
	type Modified = Partial<New> & Pick<NonEditableProperties, "id">;

	interface PaymentAccount {
		company: { id: number; name: Lang };
		taxiService: {
			id: number;
			settlement: Lang;
			company: { id: number; name: Lang };
		};
	}

	type PaymentAccounts = PaymentAccount[];

	interface Response extends NonEditableProperties {
		active: boolean;
		configuration: AssetPaymentConfiguration | MaibPaymentConfiguration;
		isDefault: boolean;
		name: string;

		withdrawMethod: WithdrawMethod;
		defaultHoldValue: number;

		paymentAccounts: PaymentAccounts;
		paymentProvider: PaymentProvider;
		prePaymentProvider: PaymentProvider;
		type: PaymentSystemFrontEndType;

		v: number;
	}
	interface BasePaymentConfig {
		systemReturnFeeToExecutor: boolean;
		systemFeePercentageForExecutor: number;
		systemFeeAdditionalForExecutor: number;
	}

	interface AssetPaymentConfiguration extends BasePaymentConfig {
		assetPaymentsKey: string;
		assetPaymentsSecretKey: string;

		assetPaymentsProcessingId: number;
		assetPaymentsFeePercentage: number;
		assetPaymentsAdditionalFee: number;
		assetPaymentsGlobalFeeFromSystem: boolean;

		assetPaymentsCurrency: PaymentCurrency;
		assetPaymentsCountryIso: PaymentCountryIso;

		assetCheck: boolean;
		assetAction: boolean;
	}

	interface MaibPaymentConfiguration extends BasePaymentConfig {
		maibPaymentsKey: string;
		maibPaymentsSecretKey: string;
		maibSignatureId: string;

		maibPaymentsCurrency: MaibPaymentCurrency;
		maibPaymentsCountryIso: MaibPaymentCountryIso;

		maibCheck: boolean;
		maibAction: boolean;

		maibWithdrawMethod: WithdrawMethod;
	}

	export interface AssetPaymentConfigurationPayload {
		assetPaymentsKey: string;
		assetPaymentsSecretKey: string;
		assetPaymentsProcessingId: number;
		assetPaymentsCurrency: string;
		assetPaymentsCountryIso: string;
	}

	export interface MaibPaymentConfigurationPayload {
		maibPaymentsKey: string;
		maibPaymentsSecretKey: string;
		maibSignatureId: string;
		maibPaymentsCurrency: string;
		maibPaymentsCountryIso: string;
	}

	interface AssetCheckConnection {
		provider: PaymentProvider.ASSETPAYMENT;
		configuration: AssetPaymentConfigurationPayload;
	}

	interface MaibCheckConnection {
		provider: PaymentProvider.MAIB;
		configuration: MaibPaymentConfigurationPayload;
	}

	export type CheckConnectionRequestPayload =
		| AssetCheckConnection
		| MaibCheckConnection;

	type UpdatePaymentSystemParams = {
		configuration: AssetPaymentConfiguration | MaibPaymentConfiguration;
		name?: string;
		active?: boolean;
	};
}
export enum PaymentCurrency {
	USD = "USD",
	EUR = "EUR",
	UAH = "UAH",
	NULL = "NULL",
}

export enum PaymentCountryIso {
	UKR = "UKR",
	POL = "POL",
	AZE = "AZE",
	NULL = "NULL",
}

export enum MaibPaymentCurrency {
	USD = "USD",
	EUR = "EUR",
	MDL = "MDL",
}

export enum MaibPaymentCountryIso {
	MDA = "MDA",
}

export enum PaymentProvider {
	ASSETPAYMENT = "assetPayments",
	MAIB = "maib",
}

export enum WithdrawMethod {
	WITHDRAW = "withdraw",
	WITHDRAW_WITH_HOLD = "withdraw_with_hold",
}
export enum PaymentSystemFrontEndType {
	CUSTOMER = "customer",
	EXECUTOR = "executor",
	TEMPLATE_CUSTOMER = "template_customer",
	TEMPLATE_EXECUTOR = "template_executor",
}

export enum PaymentRegistrationType {
	FRAME = "frame",
	URL = "url",
}

export enum NoticeDestinationApp {
	EXECUTOR = "EXECUTOR",
	CUSTOMER = "CUSTOMER",
}

export default PaymentSystem;
